import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { type SortMode, sortLines } from "../lib/sort";

const SAMPLE = "banana\napple\ncherry\ndate\nelderberry";

const selectClass =
  "h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-border accent-primary"
      />
      {label}
    </label>
  );
}

export function LineSorterTool() {
  const uid = useId();
  const [input, setInput] = useState(SAMPLE);
  const [mode, setMode] = useState<SortMode>("alpha");
  const [reverse, setReverse] = useState(false);
  const [caseInsensitive, setCaseInsensitive] = useState(false);
  const [removeEmpty, setRemoveEmpty] = useState(false);
  const [copied, setCopied] = useState(false);

  const result = useMemo(
    () => sortLines(input, { mode, reverse, caseInsensitive, removeEmpty }),
    [input, mode, reverse, caseInsensitive, removeEmpty],
  );

  const copy = async () => {
    if (!result.text) return;
    try {
      await navigator.clipboard.writeText(result.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-in`}>{m.ls_input_label()}</Label>
          <textarea
            id={`${uid}-in`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={m.ls_placeholder()}
            spellCheck={false}
            className="min-h-[14rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor={`${uid}-out`}>{m.ls_output_label()}</Label>
            <div className="flex items-center gap-3">
              <span
                data-testid="ls-count"
                className="text-xs text-muted-foreground"
              >
                {m.ls_lines({ n: String(result.count) })}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={copy}
                disabled={!result.text}
                className="gap-2"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-emerald-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                {copied ? m.ls_copied() : m.ls_copy()}
              </Button>
            </div>
          </div>
          <textarea
            id={`${uid}-out`}
            readOnly
            data-testid="ls-output"
            value={result.text}
            spellCheck={false}
            className="min-h-[14rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-x-6 gap-y-3 rounded-xl border border-border bg-card/40 p-4">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-mode`}>{m.ls_mode()}</Label>
          <select
            id={`${uid}-mode`}
            className={selectClass}
            value={mode}
            onChange={(e) => setMode(e.target.value as SortMode)}
          >
            <option value="alpha">{m.ls_mode_alpha()}</option>
            <option value="numeric">{m.ls_mode_numeric()}</option>
            <option value="length">{m.ls_mode_length()}</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-3 pb-2.5">
          <Toggle
            checked={reverse}
            onChange={setReverse}
            label={m.ls_reverse()}
          />
          <Toggle
            checked={caseInsensitive}
            onChange={setCaseInsensitive}
            label={m.ls_case()}
          />
          <Toggle
            checked={removeEmpty}
            onChange={setRemoveEmpty}
            label={m.ls_empty()}
          />
        </div>
      </div>
    </div>
  );
}
